import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useToastActions } from '@shared/ui/toast';
import { useAuthOverlay } from './AuthOverlayContext';

export interface RequireAuthOptions {
  reason?: string;
  returnTo?: string;
  onAuthenticated?: () => void;
  anchorPoint?: { x: number; y: number };
}

export const useRequireAuth = () => {
  const { authState } = useAuth();
  const { t } = useTranslation('auth');
  const toast = useToastActions();
  const { openAuthSheet, showAuthNudge } = useAuthOverlay();

  const requireAuth = useCallback(
    (options: RequireAuthOptions = {}): boolean => {
      if (authState.isAuthenticated && authState.user) return true;

      const returnTo =
        options.returnTo ?? window.location.pathname + window.location.search;
      const reason = options.reason ?? t('authSheet.signInToContinue', 'Sign in to continue');

      if (options.anchorPoint) {
        showAuthNudge({
          message: reason,
          position: options.anchorPoint,
          actionLabel: t('authSheet.signInAction', 'Sign in'),
          returnTo,
          reason,
          onAuthenticated: options.onAuthenticated
        });
        return false;
      }

      toast.info(reason, undefined, {
        duration: 6500,
        position: 'bottom-center',
        action: {
          label: t('authSheet.signInAction', 'Sign in'),
          onClick: () =>
            openAuthSheet({
              returnTo,
              reason,
              onAuthenticated: options.onAuthenticated
            })
        }
      });

      return false;
    },
    [
      authState.isAuthenticated,
      authState.user,
      openAuthSheet,
      showAuthNudge,
      toast
    ]
  );

  return { requireAuth };
};

export default useRequireAuth;
